import { format, startOfMonth } from "date-fns";
import { useGetList } from "ra-core";
import { useMemo } from "react";

import { useDeliveries } from "@/components/fretum/delivery/useDelivery";

import { useConfigurationContext } from "../../root/ConfigurationContext";
import { collectBuckets } from "./homeModel";
import type { Subscription, TimeEntry } from "./homeTypes";
import {
  deliveryRows,
  fixedCosts,
  funnelByStage,
  hoursThisMonth,
  invoicedThisMonth,
  openPipeline,
  overdueClosings,
  revenueByMonth,
  wonThisMonth,
} from "./pulseModel";
import {
  useHomeInvoices,
  useOpenDeals,
  useOpenMilestones,
} from "./useHomeData";

/**
 * Everything view B needs. Optional hub resources (time entries,
 * subscriptions, deliveries, invoices) report `null` when the data provider
 * does not have them, so their widget is omitted instead of showing zeros.
 */
export const usePulseData = () => {
  const { dealStages, dealPipelineStatuses } = useConfigurationContext();
  const monthStart = useMemo(
    () => format(startOfMonth(new Date()), "yyyy-MM-dd"),
    [],
  );
  const deals = useOpenDeals();
  const invoices = useHomeInvoices();
  const milestones = useOpenMilestones();
  const deliveries = useDeliveries();
  const timeEntries = useGetList<TimeEntry>(
    "time_entries",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "date", order: "DESC" },
      filter: { "date@gte": monthStart },
    },
    { retry: false },
  );
  const subscriptions = useGetList<Subscription>(
    "subscriptions",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "next_renewal", order: "ASC" },
      filter: { status: "active" },
    },
    { retry: false },
  );

  return useMemo(() => {
    const now = new Date();
    const dealList = deals.data ?? [];
    const invoiceList = invoices.data ?? [];
    return {
      isPending: deals.isPending,
      pipeline: openPipeline(dealList, dealPipelineStatuses),
      won: wonThisMonth(dealList, dealPipelineStatuses, now),
      funnel: funnelByStage(dealList, dealStages, dealPipelineStatuses),
      overdueClosings: overdueClosings(dealList, dealPipelineStatuses, now),
      deals: dealList,
      invoicing: invoices.isError
        ? null
        : {
            month: invoicedThisMonth(invoiceList, now),
            revenue: revenueByMonth(invoiceList, now),
            collect: collectBuckets(invoiceList, now),
            outstandingCount: invoiceList.filter(
              (invoice) =>
                invoice.status === "issued" || invoice.status === "overdue",
            ).length,
          },
      deliveries: deliveries.isError
        ? null
        : deliveryRows(deliveries.data ?? [], dealList, milestones.data ?? []),
      hours: timeEntries.isError
        ? null
        : hoursThisMonth(timeEntries.data ?? [], now),
      costs: subscriptions.isError
        ? null
        : fixedCosts(subscriptions.data ?? [], now),
    };
  }, [
    deals.data,
    deals.isPending,
    invoices.data,
    invoices.isError,
    milestones.data,
    deliveries.data,
    deliveries.isError,
    timeEntries.data,
    timeEntries.isError,
    subscriptions.data,
    subscriptions.isError,
    dealStages,
    dealPipelineStatuses,
  ]);
};

export type PulseData = ReturnType<typeof usePulseData>;
